import axios from "axios";
import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import * as yup from "yup";
import {
  acceptTeamInvitationConstants,
  accountConfirmationConstants,
  authUrls,
  authValidationConstants,
  identityApiBaseUrl,
} from "../../constants";

import {
  useHookForm,
  useMerchantApiMutation,
  useQueryString,
} from "../../hooks";
import {
  IHookForm,
  IRegisterWithInvitationCode,
  IRegisterResponse,
} from "../../interfaces-and-types";
import { globalHandlerUtils } from "../../utils";
import { View } from "./View";

export interface ISignUpWithInvitationCodeViewProps
  extends IHookForm<IRegisterWithInvitationCode> {
  formValues: IRegisterWithInvitationCode;
  isPasswordTouched: boolean;
  signUpSuccessful: boolean;
  redirectToLogin: () => void;
  targetPageOnLogin: string;
}

export const SignUpWithInvitationCode = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // get the form values from the url
  const query = useQueryString();

  const emailQueryString = query.get(
    accountConfirmationConstants.emailQueryStringName
  );
  const invitationCodeQueryString = query.get(
    acceptTeamInvitationConstants.invitationCodeQueryStringName
  );

  const {
    // isLoading: isLoadingRegister,
    // isError: isErrorRegister,
    // data: apiResponseRegister,
    isSuccess: isSuccessRegister,
    mutate: signUp,
  } = useMerchantApiMutation<IRegisterWithInvitationCode, IRegisterResponse>(
    "registerWithInvitationCode"
  ).usePostMutation;

  const initialFormValues: IRegisterWithInvitationCode = {
    email: "",
    password: "",
    confirm_password: "",
    first_name: "",
    last_name: "",
    phone_number: "",
    country: "NG",
    is_consent_checked: false,
    invitation_code: "",
  };

  const signUpSchema: yup.SchemaOf<IRegisterWithInvitationCode> = yup
    .object({
      email: yup
        .string()
        .trim()
        .email()
        .required("Email is required")
        .test(
          "email", // Name
          "Email is already taken", // Msg
          async (email, testContext) => {
            try {
              const {
                data: { is_valid, error_message },
              } = await axios.post(
                identityApiBaseUrl + authUrls.remoteValidateEmail,
                { email }
              );

              if (!is_valid)
                return testContext.createError({ message: error_message });

              return is_valid;
            } catch (error) {
              // console.log("throwing email network error logs");
              testContext.createError({ message: "No network" });
            }
          }
        ),
      password: yup
        .string()
        .required("Password is required")
        .min(
          authValidationConstants.minPasswordLength,
          ({ min }) => `Password must be at least ${min} characters`
        )
        .max(
          authValidationConstants.maxPasswordLength,
          ({ max }) => `Password must have a max of ${max} characters`
        )
        .matches(/\w*[a-z]\w*/, "Password must have a small letter")
        .matches(/\w*[A-Z]\w*/, "Password must have a capital letter")
        .matches(/\d/, "Password must have a number")
        .matches(
          /[!@#$%^&*()\-_"=+{};]/,
          `Password must have a special character`
        )
        .test(
          "password", // Name
          () => errors.password?.message, // Msg
          async (password, testContext) => {
            try {
              const {
                data: { is_valid, error_message },
              } = await axios.post(
                identityApiBaseUrl + authUrls.remoteValidatePassword,
                { password }
              );

              if (!is_valid)
                return testContext.createError({ message: error_message });

              return is_valid;
            } catch (error) {
              // console.log("throwing password network error logs");
              testContext.createError({ message: "No network" });
            }
          }
        ),
      confirm_password: yup
        .string()
        .oneOf(
          [yup.ref("password"), null],
          "Password and confirmation must match"
        )
        .required("Password confirmation is required"),
      first_name: yup.string().required("First Name is required"),
      last_name: yup.string().required("Last Name is required"),
      phone_number: yup.string().required("Phone Number is required"),
      country: yup.string().required("Country is required"),
      is_consent_checked: yup
        .boolean()
        .required()
        .isTrue("You must agree to the terms and conditions"),
      invitation_code: yup.string().required("Invitation code is required"),
    })
    .required();

  useEffect(() => {
    const allQueryStringValuesRetrieved =
      !!emailQueryString && !!invitationCodeQueryString;

    if (allQueryStringValuesRetrieved) {
      reset({
        ...formValues,
        email: emailQueryString,
        invitation_code: invitationCodeQueryString,
      });
    }
  }, [emailQueryString, invitationCodeQueryString]);

  const handleApiSubmit = (data: IRegisterWithInvitationCode) => {
    signUp(data);
  };

  const targetPageOnLogin = `/login${location.search}`;
  const redirectToLoginPreserveParams = () => {
    navigate(targetPageOnLogin);
  };
  const handleError = (errors: any) => globalHandlerUtils.handleError(errors);

  const { onFormSubmit, errors, register, trigger, watch, reset } = useHookForm(
    {
      initialFormValues,
      validationSchema: signUpSchema,
      onValid: handleApiSubmit,
      onInvalid: handleError,
      mode: "all",
    }
  );

  useEffect(() => {
    // if email already exists, redirect to login
    if (errors["email"]?.message?.includes("already exists"))
      return redirectToLoginPreserveParams();
  }, [errors["email"]?.message]);

  const formValues = watch();
  const isPasswordTouched = !!formValues.password;

  useEffect(() => {
    trigger("email"); // trigger validation on load to check email availability
  }, [formValues.email]);

  return (
    <View
      signUpSuccessful={isSuccessRegister}
      redirectToLogin={redirectToLoginPreserveParams}
      targetPageOnLogin={targetPageOnLogin}
      formValues={formValues}
      isPasswordTouched={isPasswordTouched}
      handleSubmit={onFormSubmit}
      register={register}
      errors={errors}
    />
  );
};
